import React from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import { LoginPage } from '@/pages/auth/login';
import { ActivationPage } from '@/pages/auth/ActivationPage';
import { ForgotPasswordPage } from '@/pages/auth/ForgotPasswordPage';
import { MfaPage } from '@/pages/auth/MfaPage';
import { MfaSetupPage } from '@/pages/auth/MfaSetupPage';
import { ResetPasswordPage } from '@/pages/auth/ResetPasswordPage';
import { SessionExpiredPage } from '@/pages/auth/SessionExpiredPage';
import { PrivacyPolicyPage } from '@/pages/public/PrivacyPolicyPage';
import { ResidentSelfRegistrationPage } from '@/pages/public/ResidentSelfRegistrationPage';
import { TermsPage } from '@/pages/public/TermsPage';
import { ResidentLayout } from '@/layouts/ResidentLayout';
import { PmLayout } from '@/layouts/PmLayout';
import { RoleProtectedRoute } from './RoleProtectedRoute';
import { GuardOrDeviceRoute } from './GuardOrDeviceRoute';
import { PmOnlyRoute } from './PmOnlyRoute';

// Resident pages
import { AccountPage } from '@/pages/resident/AccountPage';
import { FavouritesPage } from '@/pages/resident/FavouritesPage';
import { HomePage } from '@/pages/resident/HomePage';
import { InviteVisitorPage } from '@/pages/resident/InviteVisitorPage';
import { EventsPage } from '@/pages/resident/EventsPage';
import { MyVisitorsPage } from '@/pages/resident/MyVisitorsPage';
import { NotificationPreferencesPage } from '@/pages/resident/NotificationPreferencesPage';
import { NotificationsPage } from '@/pages/resident/NotificationsPage';
import { DeliveriesPage } from '@/pages/resident/DeliveriesPage';
import { PassesPage } from '@/pages/resident/PassesPage';
import { PassHistoryPage } from '@/pages/resident/PassHistoryPage';
import { PrivacyPage } from '@/pages/resident/PrivacyPage';
import { WalkInApprovalPage } from '@/pages/resident/WalkInApprovalPage';

// Property Manager pages
import { DashboardPage } from '@/pages/manager/DashboardPage';
import { AccessLogPage } from '@/pages/manager/AccessLogPage';
import { IncidentsPage } from '@/pages/manager/IncidentsPage';
import { CrewSessionsPage } from '@/pages/manager/CrewSessionsPage';
import { UsersPage } from '@/pages/manager/UsersPage';
import { CompaniesPage } from '@/pages/manager/CompaniesPage';
import { ModulesPage } from '@/pages/manager/ModulesPage';
import { PropertySetupPage } from '@/pages/manager/PropertySetupPage';
import { GuardsPage } from '@/pages/manager/GuardsPage';
import { TrustedOperatorsPage } from '@/pages/manager/TrustedOperatorsPage';
import { AnnouncementsPage } from '@/pages/manager/AnnouncementsPage';
import { CompliancePage } from '@/pages/manager/CompliancePage';
import { AnalyticsPage } from '@/pages/manager/AnalyticsPage';
import { EmergencyPage } from '@/pages/manager/EmergencyPage';
import { ManagerAccountPage } from '@/pages/manager/ManagerAccountPage';

import { EnterpriseLayout } from '@/layouts/EnterpriseLayout';
import { GuardLayout } from '@/layouts/GuardLayout';

// Guard pages
import { GateQueuePage } from '@/pages/guard/GateQueuePage';
import { WalkInPage } from '@/pages/guard/WalkInPage';
import { TrustedOperatorEntryPage } from '@/pages/guard/TrustedOperatorEntryPage';
import { IncidentPage } from '@/pages/guard/IncidentPage';
import { ActivityLogPage } from '@/pages/guard/ActivityLogPage';
import { ShiftStartPage } from '@/pages/guard/ShiftStartPage';
import { DevicePairingPage } from '@/pages/guard/DevicePairingPage';
import { ShiftSelectionPage } from '@/pages/guard/ShiftSelectionPage';

// Enterprise Tenant Admin pages
import { EnterprisePage } from '@/pages/enterprise/EnterprisePage';
import { EnterpriseVisitorsPage } from '@/pages/enterprise/EnterpriseVisitorsPage';
import { EnterpriseHistoryPage } from '@/pages/enterprise/EnterpriseHistoryPage';
import { EnterpriseWorkerPassesPage } from '@/pages/enterprise/EnterpriseWorkerPassesPage';
import { EnterpriseEmployeesPage } from '@/pages/enterprise/EnterpriseEmployeesPage';
import { EnterpriseEventsPage } from '@/pages/enterprise/EnterpriseEventsPage';
import { EnterpriseAccountPage } from '@/pages/enterprise/EnterpriseAccountPage';

import { EmployeeLayout } from '@/layouts/EmployeeLayout';
import { EmployeeHomePage } from '@/pages/employee/EmployeeHomePage';
import { EmployeeVisitorsPage } from '@/pages/employee/EmployeeVisitorsPage';
import { EmployeeHistoryPage } from '@/pages/employee/EmployeeHistoryPage';
import { EmployeeEventsPage } from '@/pages/employee/EmployeeEventsPage';
import { EmployeeAccountPage } from '@/pages/employee/EmployeeAccountPage';

import { OrgAdminLayout } from '@/layouts/OrgAdminLayout';
import { OrgAdminHomePage } from '@/pages/org/OrgAdminHomePage';
import { OrgPropertiesPage } from '@/pages/org/OrgPropertiesPage';
import { OrgAdminsPage } from '@/pages/org/OrgAdminsPage';
import { OrgCompliancePage } from '@/pages/org/OrgCompliancePage';

import { PlatformProtectedRoute } from './PlatformProtectedRoute';
import { SuperAdminLayout } from '@/layouts/SuperAdminLayout';
import { SuperAdminHomePage } from '@/pages/admin/SuperAdminHomePage';
import { SuperAdminOrgsPage } from '@/pages/admin/SuperAdminOrgsPage';
import { SuperAdminFeatureFlagsPage } from '@/pages/admin/SuperAdminFeatureFlagsPage';
import { SuperAdminImpersonationPage } from '@/pages/admin/SuperAdminImpersonationPage';
import { SuperAdminConfigPage } from '@/pages/admin/SuperAdminConfigPage';
import { SuperAdminAuditLogPage } from '@/pages/admin/SuperAdminAuditLogPage';
import { SuperAdminNotificationsPage } from '@/pages/admin/SuperAdminNotificationsPage';

/**
 * Application route tree.
 *
 * Auth screens:     /login, /mfa, /forgot-password, /reset-password
 * Resident:         /resident/* — requires auth (any role)
 * Property Manager: /manager/* — requires property_manager or security_supervisor role
 */
export function AppRoutes(): React.JSX.Element {
  return (
    <Routes>
      {/* Auth */}
      <Route path="/" element={<Navigate to="/login" replace />} />
      {/* Defensive redirect: /auth/login is the API path, not the frontend path */}
      <Route path="/auth/login" element={<Navigate to="/login" replace />} />
      <Route path="/auth/*" element={<Navigate to="/login" replace />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/mfa" element={<MfaPage />} />
      <Route path="/mfa-setup" element={<MfaSetupPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/activate" element={<ActivationPage />} />
      <Route path="/register/:slug" element={<ResidentSelfRegistrationPage />} />
      <Route path="/session-expired" element={<SessionExpiredPage />} />

      {/* Public legal pages — KDPA §29 right to be informed */}
      <Route path="/privacy" element={<PrivacyPolicyPage />} />
      <Route path="/terms" element={<TermsPage />} />

      {/* Resident dashboard */}
      <Route
        element={
          <RoleProtectedRoute roles={['resident']}>
            <ResidentLayout />
          </RoleProtectedRoute>
        }
      >
        <Route path="/resident" element={<HomePage />} />
        {/* Common deep-link alias — links from mobile notifications use /resident/dashboard */}
        <Route path="/resident/dashboard" element={<Navigate to="/resident" replace />} />
        <Route path="/resident/visitors" element={<MyVisitorsPage />} />
        <Route path="/resident/invite" element={<InviteVisitorPage />} />
        <Route path="/resident/history" element={<PassHistoryPage />} />
        <Route path="/resident/deliveries" element={<DeliveriesPage />} />
        <Route path="/resident/passes" element={<PassesPage />} />
        <Route path="/resident/favourites" element={<FavouritesPage />} />
        <Route path="/resident/events" element={<EventsPage />} />
        <Route path="/resident/walk-in/:requestId" element={<WalkInApprovalPage />} />
        <Route path="/resident/notifications" element={<NotificationsPage />} />
        <Route path="/resident/account" element={<AccountPage />} />
        <Route path="/resident/account/notifications" element={<NotificationPreferencesPage />} />
        <Route path="/resident/account/privacy" element={<PrivacyPage />} />
      </Route>

      {/* Property Manager / Security Supervisor dashboard */}
      <Route
        element={
          <RoleProtectedRoute roles={['property_manager', 'security_supervisor']}>
            <PmLayout />
          </RoleProtectedRoute>
        }
      >
        <Route path="/manager" element={<DashboardPage />} />
        <Route path="/manager/access-log" element={<AccessLogPage />} />
        <Route path="/manager/incidents" element={<IncidentsPage />} />
        <Route path="/manager/crew-sessions" element={<CrewSessionsPage />} />
        <Route path="/manager/guards" element={<GuardsPage />} />
        <Route path="/manager/trusted-operators" element={<TrustedOperatorsPage />} />
        <Route path="/manager/users" element={<UsersPage />} />
        <Route path="/manager/companies" element={<CompaniesPage />} />
        <Route
          path="/manager/modules"
          element={
            <PmOnlyRoute>
              <ModulesPage />
            </PmOnlyRoute>
          }
        />
        <Route
          path="/manager/property"
          element={
            <PmOnlyRoute>
              <PropertySetupPage />
            </PmOnlyRoute>
          }
        />
        <Route
          path="/manager/compliance"
          element={
            <PmOnlyRoute>
              <CompliancePage />
            </PmOnlyRoute>
          }
        />
        <Route path="/manager/announcements" element={<AnnouncementsPage />} />
        <Route path="/manager/analytics" element={<AnalyticsPage />} />
        <Route path="/manager/emergency" element={<EmergencyPage />} />
        <Route path="/manager/notifications" element={<NotificationsPage />} />
        <Route path="/manager/account" element={<ManagerAccountPage />} />
        <Route path="/manager/account/privacy" element={<PrivacyPage />} />
      </Route>

      {/* Enterprise Tenant Admin dashboard */}
      <Route
        element={
          <RoleProtectedRoute roles={['enterprise_tenant_admin']}>
            <EnterpriseLayout />
          </RoleProtectedRoute>
        }
      >
        <Route path="/enterprise" element={<EnterprisePage />} />
        <Route path="/enterprise/visitors" element={<EnterpriseVisitorsPage />} />
        <Route path="/enterprise/history" element={<EnterpriseHistoryPage />} />
        <Route path="/enterprise/worker-passes" element={<EnterpriseWorkerPassesPage />} />
        <Route path="/enterprise/employees" element={<EnterpriseEmployeesPage />} />
        <Route path="/enterprise/events" element={<EnterpriseEventsPage />} />
        <Route path="/enterprise/notifications" element={<NotificationsPage />} />
        <Route path="/enterprise/account" element={<EnterpriseAccountPage />} />
        <Route path="/enterprise/account/privacy" element={<PrivacyPage />} />
      </Route>

      {/* Employee Dashboard */}
      <Route
        element={
          <RoleProtectedRoute roles={['employee']}>
            <EmployeeLayout />
          </RoleProtectedRoute>
        }
      >
        <Route path="/employee" element={<EmployeeHomePage />} />
        <Route path="/employee/visitors" element={<EmployeeVisitorsPage />} />
        <Route path="/employee/history" element={<EmployeeHistoryPage />} />
        <Route path="/employee/events" element={<EmployeeEventsPage />} />
        <Route path="/employee/notifications" element={<NotificationsPage />} />
        <Route path="/employee/account" element={<EmployeeAccountPage />} />
        <Route path="/employee/account/privacy" element={<PrivacyPage />} />
      </Route>

      {/* Guard Device Pairing & Shift Selection (Mode B — no user auth required) */}
      <Route path="/guard/pair" element={<DevicePairingPage />} />
      <Route path="/guard/select" element={<ShiftSelectionPage />} />

      {/* Guard Dashboard — accessible via JWT (Mode A) OR device session (Mode B) */}
      <Route
        element={
          <GuardOrDeviceRoute>
            <GuardLayout />
          </GuardOrDeviceRoute>
        }
      >
        <Route path="/guard" element={<GateQueuePage />} />
        <Route path="/guard/walk-in" element={<WalkInPage />} />
        <Route path="/guard/trusted-operator" element={<TrustedOperatorEntryPage />} />
        <Route path="/guard/incidents" element={<IncidentPage />} />
        <Route path="/guard/activity" element={<ActivityLogPage />} />
        <Route path="/guard/shift-start" element={<ShiftStartPage />} />
      </Route>

      {/* Org Admin Dashboard */}
      <Route
        element={
          <RoleProtectedRoute roles={['org_admin']}>
            <OrgAdminLayout />
          </RoleProtectedRoute>
        }
      >
        <Route path="/org" element={<OrgAdminHomePage />} />
        <Route path="/org/properties" element={<OrgPropertiesPage />} />
        <Route path="/org/admins" element={<OrgAdminsPage />} />
        <Route path="/org/notifications" element={<NotificationsPage />} />
        <Route path="/org/compliance" element={<OrgCompliancePage />} />
        <Route path="/org/account/privacy" element={<PrivacyPage />} />
      </Route>

      {/* Super Admin Console */}
      <Route element={<PlatformProtectedRoute platform="super_admin" />}>
        <Route element={<SuperAdminLayout />}>
          <Route path="/admin" element={<SuperAdminHomePage />} />
          <Route path="/admin/organisations" element={<SuperAdminOrgsPage />} />
          <Route path="/admin/feature-flags" element={<SuperAdminFeatureFlagsPage />} />
          <Route path="/admin/impersonation" element={<SuperAdminImpersonationPage />} />
          <Route path="/admin/config" element={<SuperAdminConfigPage />} />
          <Route path="/admin/audit-log" element={<SuperAdminAuditLogPage />} />
          <Route path="/admin/failed-notifications" element={<SuperAdminNotificationsPage />} />
          <Route path="/admin/notifications" element={<NotificationsPage />} />
        </Route>
      </Route>

      <Route
        path="*"
        element={
          <div className="flex min-h-screen items-center justify-center">
            <p className="text-muted-foreground">404 — Page not found</p>
          </div>
        }
      />
    </Routes>
  );
}
